import React, { memo, useMemo, useState } from "react";
import { ArrowLeft, Armchair, Flag, LogIn, LogOut, Pencil, Play, Plus } from "lucide-react";
import { cleanGameEvents } from "../../timeline";
import { buildNight, MetroChart, PotChart, slotColor } from "./timeline-charts";
import "./timeline-editor.css";
import "./timeline-detail.css";

const clock = (t) => new Date(t).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

function formatLength(ms) {
  const mins = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${m} min` : `${h} h`;
}

const ICONS = { start: Play, buyin: Armchair, rebuy: Plus, out: LogOut, back: LogIn, end: Flag };

// Full-page view of a saved game's timeline: the night's numbers, the pot
// growing through the night, a metro map of every player's night, each
// player's time at the table, and every move in order. Read-only; Edit hands
// over to the timeline editor.
// `buyIns` holds each player's saved buy-in count, the source of truth for
// money and rebuys (events only place them in time).
export const TimelineDetail = memo(function TimelineDetail({ title, startedAt, endedAt, events, playerIds, buyIns, buyInAmount, nets, getName, renderAvatar, formatMoney, onEdit, onClose }) {
  const clean = useMemo(() => cleanGameEvents(events), [events]);

  const players = useMemo(() => playerIds.map((pid) => {
    const own = clean.filter((ev) => ev.pid === pid);
    const seat = own.find((ev) => ev.type === "buyin")?.t ?? startedAt;
    const count = Math.max(1, buyIns?.[pid] || own.filter((ev) => ev.type === "rebuy").length + 1);
    // Time at the table: from the seat to each "out", resuming at "back".
    let played = 0;
    let since = seat;
    let outAt = null;
    for (const ev of own) {
      if (ev.type === "out" && since !== null) { played += ev.t - since; since = null; outAt = ev.t; }
      else if (ev.type === "back" && since === null) { since = ev.t; outAt = null; }
    }
    if (since !== null) played += endedAt - since;
    return { pid, name: getName(pid) || "Unknown player", seat, rebuys: count - 1, played, outAt, buyIns: count };
  // Still in at the end first, then the latest to go out; bigger buy-ins first on ties.
  }).sort((a, b) => ((a.outAt !== null) - (b.outAt !== null)) || ((b.outAt ?? 0) - (a.outAt ?? 0)) || (b.buyIns - a.buyIns)), [clean, playerIds, buyIns, startedAt, endedAt, getName]);

  const feed = useMemo(() => {
    const count = {};
    const rows = [{ key: "start", type: "start", t: startedAt, text: "Game started" }];
    for (const ev of clean) {
      const name = getName(ev.pid) || "Unknown player";
      let text = "";
      if (ev.type === "buyin") { count[ev.pid] = 1; text = `${name} sat down`; }
      else if (ev.type === "rebuy") { count[ev.pid] = (count[ev.pid] || 1) + 1; text = `${name} rebought (buy-in ${count[ev.pid]})`; }
      else if (ev.type === "out") text = `${name} went out`;
      else if (ev.type === "back") text = `${name} came back in`;
      else continue;
      rows.push({ key: ev.id, type: ev.type, t: ev.t, text, pid: ev.pid });
    }
    rows.push({ key: "end", type: "end", t: endedAt, text: "Game ended" });
    return rows;
  }, [clean, startedAt, endedAt, getName]);

  const night = useMemo(
    () => buildNight({ events: clean, playerIds, buyIns, buyInAmount, startedAt, endedAt, getName }),
    [clean, playerIds, buyIns, buyInAmount, startedAt, endedAt, getName]
  );
  const slotOf = useMemo(() => Object.fromEntries(night.list.map((p) => [p.pid, p.slot])), [night]);
  // The moment picked on either chart (null: the end of the night).
  const [scrubT, setScrubT] = useState(null);
  const moment = scrubT === null ? null : [...night.moments].reverse().find((m) => m.t === scrubT);

  const totalRebuys = players.reduce((sum, p) => sum + p.rebuys, 0);
  const pot = players.reduce((sum, p) => sum + p.buyIns * (buyInAmount || 0), 0);
  const stillIn = players.filter((p) => p.outAt === null).length;

  return (
    <div className="profile-page-overlay tl-edit-overlay">
      <div className="profile-page">
        <div className="profile-page-topbar">
          <button className="profile-page-back" aria-label="Back" onClick={onClose}>
            <ArrowLeft size={20} />
          </button>
          <div className="tl-edit-title">
            <span className="profile-page-topbar-name">Timeline</span>
            {title && <span className="tl-edit-subtitle">{title}</span>}
          </div>
          {onEdit && (
            <button type="button" className="section-label-action tld-edit" onClick={onEdit}>
              <Pencil size={14} aria-hidden="true" /> Edit
            </button>
          )}
        </div>
        <div className="profile-page-content tl-edit-content">
          <div className="tld-stats">
            <div className="tld-stat"><span>Started</span><strong>{clock(startedAt)}</strong></div>
            <div className="tld-stat"><span>Ended</span><strong>{clock(endedAt)}</strong></div>
            <div className="tld-stat"><span>Length</span><strong>{formatLength(endedAt - startedAt)}</strong></div>
            <div className="tld-stat"><span>Pot</span><strong>{formatMoney(pot)}</strong></div>
            <div className="tld-stat"><span>Rebuys</span><strong>{totalRebuys}</strong></div>
            <div className="tld-stat"><span>At the end</span><strong>{stillIn} of {players.length}</strong></div>
          </div>

          <div className="breakdown-section-label">The pot</div>
          <div className="tld-card tld-viz">
            <div className="tld-readout" aria-live="polite">
              <span className="tld-readout-value">{formatMoney(scrubT === null ? night.pot : night.potAt(scrubT))}</span>
              <span className="tld-readout-note">
                {scrubT === null
                  ? `Final pot · ${players.length} players · tap or drag the chart`
                  : `${clock(scrubT)}${moment ? ` · ${moment.text}` : ""} · ${night.playingAt(scrubT)} playing`}
              </span>
              {scrubT !== null && (
                <button type="button" className="tld-readout-clear" onClick={() => setScrubT(null)}>Reset</button>
              )}
            </div>
            <PotChart night={night} scrubT={scrubT} onScrub={setScrubT} formatMoney={formatMoney} />
          </div>

          <div className="breakdown-section-label">The night</div>
          <div className="tld-card tld-viz">
            <MetroChart night={night} scrubT={scrubT} onScrub={setScrubT} renderAvatar={renderAvatar} formatMoney={formatMoney} />
            <div className="tld-key-row" aria-hidden="true">
              <span><i className="tld-key tld-key--seat" />Sat down</span>
              <span><i className="tld-key tld-key--rebuy" />Rebuy</span>
              <span><i className="tld-key tld-key--out">×</i>Out</span>
              <span><i className="tld-key tld-key--end" />Still in at the end</span>
            </div>
          </div>

          <div className="breakdown-section-label">Players</div>
          <div className="tl-group">
            {players.map((p) => {
              const net = nets?.[p.pid];
              return (
                <div key={p.pid} className="tl-row tld-player">
                  <i className="tld-swatch tld-swatch--row" style={{ background: slotColor(slotOf[p.pid] ?? null) }} aria-hidden="true" />
                  {renderAvatar && <span className="tl-player-avatar">{renderAvatar(p.pid, p.name)}</span>}
                  <span className="tld-player-id">
                    <span className="tl-player-name">{p.name}</span>
                    <span className="tl-player-summary">
                      {clock(p.seat)} – {p.outAt !== null ? clock(p.outAt) : "end"} · {formatLength(p.played)}
                    </span>
                    <span className="tl-player-summary">
                      {p.outAt !== null ? "Went out" : "Played to the end"}
                      {p.rebuys > 0 ? ` · ${p.rebuys} ${p.rebuys === 1 ? "rebuy" : "rebuys"}` : ""}
                    </span>
                  </span>
                  <span className="tld-player-money">
                    <span>{formatMoney(p.buyIns * (buyInAmount || 0))}</span>
                    {typeof net === "number" && (
                      <span className={net > 0 ? "tld-net tld-net--up" : net < 0 ? "tld-net tld-net--down" : "tld-net"}>
                        {net > 0 ? "+" : ""}{formatMoney(net)}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="breakdown-section-label">Play by play</div>
          <ol className="tl-group tld-feed">
            {feed.map((row) => {
              const Icon = ICONS[row.type] || Plus;
              return (
                <li key={row.key} className="tl-row tld-feed-row">
                  <span className={`tl-icon tl-icon--${row.type}`} aria-hidden="true"><Icon size={14} /></span>
                  <span className="tld-feed-text">{row.text}</span>
                  <span className="tld-feed-time">{clock(row.t)}</span>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </div>
  );
});
